import { useState, useEffect, useMemo } from 'react';
import { eventsApi } from '../../lib/api';
import BulkSelectToolbar from './BulkSelectToolbar';
import LifecycleFilterPills from './LifecycleFilterPills';
import RejectionNoteModal from './RejectionNoteModal';

const cardStyle = { background: 'rgba(255,255,255,0.97)', border: '1px solid #E2E8F0', borderRadius: 20, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', padding: 24 };

const STATUS_STYLES = {
  ai_suggested: { bg: '#F1F5F9', color: '#94A3B8', label: 'AI Suggested' },
  approved: { bg: '#ECFDF5', color: '#10B981', label: 'Approved' },
  rejected: { bg: '#FEF2F2', color: '#F97066', label: 'Rejected' },
  nda_sent: { bg: '#EEF2FF', color: '#4F46E5', label: 'NDA Sent' },
  signed: { bg: '#EEF2FF', color: '#4F46E5', label: 'Signed' },
  countersigned: { bg: '#ECFDF5', color: '#10B981', label: 'Countersigned' },
  slot_booked: { bg: '#ECFDF5', color: '#10B981', label: 'Slot Booked' },
  completed: { bg: '#E2E8F0', color: '#0F172A', label: 'Completed' },
};

const scoreColor = (score) => score >= 75 ? '#10B981' : score >= 50 ? '#F59E0B' : '#F97066';

export default function AssignmentMatrix({ eventId, onRefresh }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [lifecycleFilter, setLifecycleFilter] = useState('all');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [addingAssignment, setAddingAssignment] = useState(false);
  const [manualInventorId, setManualInventorId] = useState('');
  const [manualCompanyId, setManualCompanyId] = useState('');
  const [manualIdeaId, setManualIdeaId] = useState('');
  const [savingManual, setSavingManual] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAssignments(); }, [eventId]);

  const loadAssignments = async () => {
    try {
      const data = await eventsApi.getAssignments(eventId);
      setAssignments(data.assignments || []);
    } catch (err) { console.error('AssignmentMatrix load error:', err); }
    setLoading(false);
  };

  const handleRunMatch = async () => {
    setMatching(true);
    try {
      await eventsApi.matchSubmissions(eventId);
      await loadAssignments();
    } catch (err) { console.error('AssignmentMatrix match error:', err); }
    setMatching(false);
  };

  const handlePublish = async () => {
    if (!window.confirm('Publish all approved assignments? Inventors will be notified and NDA flow will begin.')) return;
    setPublishing(true);
    try {
      await eventsApi.publish(eventId);
      await loadAssignments();
      if (onRefresh) onRefresh();
    } catch (err) { console.error('AssignmentMatrix publish error:', err); }
    setPublishing(false);
  };

  const handleBulkApprove = async () => {
    try {
      await eventsApi.bulkUpdateAssignments(eventId, [...selectedIds], 'approved');
      setSelectedIds(new Set());
      await loadAssignments();
    } catch (err) { console.error('AssignmentMatrix bulk approve error:', err); }
  };

  const handleBulkRejectConfirm = async (note) => {
    try {
      await eventsApi.bulkUpdateAssignments(eventId, [...selectedIds], 'rejected', note);
      setSelectedIds(new Set());
      setShowRejectModal(false);
      await loadAssignments();
    } catch (err) { console.error('AssignmentMatrix bulk reject error:', err); }
  };

  const handleAddAssignment = async () => {
    if (!manualInventorId || !manualCompanyId) return;
    setSavingManual(true);
    try {
      await eventsApi.createManualAssignment(eventId, manualInventorId, manualCompanyId, manualIdeaId || null);
      setAddingAssignment(false);
      setManualInventorId('');
      setManualCompanyId('');
      setManualIdeaId('');
      await loadAssignments();
    } catch (err) { console.error('AssignmentMatrix manual assignment error:', err); }
    setSavingManual(false);
  };

  // Extract unique inventors
  const inventors = useMemo(() => {
    const seen = new Set();
    return assignments.reduce((acc, a) => {
      if (!seen.has(a.inventor_id)) {
        seen.add(a.inventor_id);
        acc.push({ id: a.inventor_id, name: a.inventor_name || a.event_submissions?.inventor_name || a.inventor_id });
      }
      return acc;
    }, []);
  }, [assignments]);

  // Extract unique companies
  const companies = useMemo(() => {
    const seen = new Set();
    return assignments.reduce((acc, a) => {
      if (!seen.has(a.company_id)) {
        seen.add(a.company_id);
        acc.push({ id: a.company_id, name: a.company_name || a.companies?.name || a.company_id });
      }
      return acc;
    }, []);
  }, [assignments]);

  // Lifecycle counts for filter pills
  const lifecycleCounts = useMemo(() => {
    const counts = {};
    assignments.forEach(a => {
      const key = a.lifecycle_status || a.status || 'ai_suggested';
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [assignments]);

  // Filter assignments by lifecycle_status
  const filteredAssignments = useMemo(() => {
    if (lifecycleFilter === 'all') return assignments;
    return assignments.filter(a => {
      const status = a.lifecycle_status || a.status || 'ai_suggested';
      return status === lifecycleFilter;
    });
  }, [assignments, lifecycleFilter]);

  // Build filtered pivot for rendering
  const filteredPivot = useMemo(() => {
    const map = new Map();
    filteredAssignments.forEach(a => {
      if (!map.has(a.inventor_id)) map.set(a.inventor_id, new Map());
      map.get(a.inventor_id).set(a.company_id, a);
    });
    return map;
  }, [filteredAssignments]);

  // Inventors with at least one filtered assignment
  const filteredInventors = useMemo(() => {
    if (lifecycleFilter === 'all') return inventors;
    const inventorIdsWithAssignments = new Set(filteredAssignments.map(a => a.inventor_id));
    return inventors.filter(inv => inventorIdsWithAssignments.has(inv.id));
  }, [inventors, filteredAssignments, lifecycleFilter]);

  const toggleInventorSelection = (inventorId) => {
    const inventorAssignmentIds = assignments
      .filter(a => a.inventor_id === inventorId)
      .map(a => a.id);
    const allSelected = inventorAssignmentIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        inventorAssignmentIds.forEach(id => next.delete(id));
      } else {
        inventorAssignmentIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const isInventorFullySelected = (inventorId) => {
    const inventorAssignmentIds = assignments
      .filter(a => a.inventor_id === inventorId)
      .map(a => a.id);
    return inventorAssignmentIds.length > 0 && inventorAssignmentIds.every(id => selectedIds.has(id));
  };

  const approvedCount = assignments.filter(a => (a.lifecycle_status || a.status) === 'approved').length;
  const publishedCount = assignments.filter(a => (a.lifecycle_status || a.status) === 'completed').length;

  if (loading) return <div style={{ textAlign: 'center', padding: 48, color: '#94A3B8' }}>Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontFamily: '"EB Garamond", serif', fontSize: 22, color: '#0F172A', margin: 0 }}>
          Matching & Assignments ({assignments.length})
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setAddingAssignment(v => !v)} style={{
            padding: '10px 20px', borderRadius: 14, background: '#EEF2FF', color: '#4F46E5',
            fontSize: 13, fontWeight: 600, fontFamily: 'Poppins, sans-serif', border: 'none', cursor: 'pointer',
          }}>
            Add Assignment
          </button>
          <button onClick={handleRunMatch} disabled={matching} style={{
            padding: '10px 20px', borderRadius: 14, background: '#0F172A', color: 'white',
            fontSize: 13, fontWeight: 600, fontFamily: 'Poppins, sans-serif', border: 'none', cursor: 'pointer',
            opacity: matching ? 0.5 : 1,
          }}>
            {matching ? 'Matching...' : assignments.length > 0 ? 'Re-run AI Match' : 'Run AI Match'}
          </button>
          {approvedCount > 0 && publishedCount === 0 && (
            <button onClick={handlePublish} disabled={publishing} style={{
              padding: '10px 20px', borderRadius: 14, background: '#c9a84c', color: 'white',
              fontSize: 13, fontWeight: 600, fontFamily: 'Poppins, sans-serif', border: 'none', cursor: 'pointer',
              opacity: publishing ? 0.5 : 1,
            }}>
              {publishing ? 'Publishing...' : `Publish ${approvedCount} Assignments`}
            </button>
          )}
        </div>
      </div>

      {/* Manual assignment form */}
      {addingAssignment && (
        <div style={{ ...cardStyle, marginBottom: 16, padding: 16 }}>
          <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: 13, fontWeight: 600, color: '#0F172A', marginBottom: 8 }}>
            Manual Assignment
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              value={manualInventorId}
              onChange={e => setManualInventorId(e.target.value)}
              placeholder="Inventor ID"
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12, fontFamily: 'Poppins, sans-serif', flex: 1, minWidth: 120 }}
            />
            <input
              value={manualCompanyId}
              onChange={e => setManualCompanyId(e.target.value)}
              placeholder="Company ID"
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12, fontFamily: 'Poppins, sans-serif', flex: 1, minWidth: 120 }}
            />
            <input
              value={manualIdeaId}
              onChange={e => setManualIdeaId(e.target.value)}
              placeholder="Idea ID (optional)"
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12, fontFamily: 'Poppins, sans-serif', flex: 1, minWidth: 120 }}
            />
            <button onClick={handleAddAssignment} disabled={savingManual || !manualInventorId || !manualCompanyId} style={{
              padding: '8px 16px', borderRadius: 8, background: '#4F46E5', color: '#fff',
              fontSize: 12, fontWeight: 600, fontFamily: 'Poppins, sans-serif', border: 'none', cursor: 'pointer',
              opacity: savingManual || !manualInventorId || !manualCompanyId ? 0.5 : 1,
            }}>
              {savingManual ? 'Adding...' : 'Add'}
            </button>
            <button onClick={() => setAddingAssignment(false)} style={{
              padding: '8px 16px', borderRadius: 8, background: '#F1F5F9', color: '#64748B',
              fontSize: 12, fontWeight: 600, fontFamily: 'Poppins, sans-serif', border: 'none', cursor: 'pointer',
            }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {assignments.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 48 }}>
          <p style={{ fontSize: 14, color: '#94A3B8', fontFamily: 'Poppins, sans-serif' }}>
            No assignments yet. Run AI matching or add assignments manually.
          </p>
        </div>
      ) : (
        <div>
          {/* Filter pills */}
          <div style={{ marginBottom: 12 }}>
            <LifecycleFilterPills
              activeFilter={lifecycleFilter}
              onFilterChange={setLifecycleFilter}
              counts={lifecycleCounts}
            />
          </div>

          {/* Bulk select toolbar */}
          {selectedIds.size > 0 && (
            <div style={{ marginBottom: 12 }}>
              <BulkSelectToolbar
                selectedCount={selectedIds.size}
                onApprove={handleBulkApprove}
                onReject={() => setShowRejectModal(true)}
                onClear={() => setSelectedIds(new Set())}
              />
            </div>
          )}

          {/* Rejection note modal */}
          {showRejectModal && (
            <div style={{ marginBottom: 12 }}>
              <RejectionNoteModal
                count={selectedIds.size}
                onReject={handleBulkRejectConfirm}
                onCancel={() => setShowRejectModal(false)}
              />
            </div>
          )}

          {/* Assignment grid */}
          <div style={{ overflowX: 'auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `160px repeat(${companies.length}, minmax(56px, 80px))`,
              gap: 1,
              background: '#E2E8F0',
              borderRadius: 12,
              overflow: 'hidden',
              minWidth: `${160 + companies.length * 60}px`,
            }}>
              {/* Header row: empty corner + company names */}
              <div style={{ background: '#F8FAFC', padding: 8 }} />
              {companies.map(c => (
                <div key={c.id} style={{
                  background: '#F8FAFC', padding: 8, fontSize: 11, fontWeight: 600,
                  color: '#0F172A', fontFamily: 'Poppins, sans-serif',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center',
                }} title={c.name}>
                  {c.name.slice(0, 10)}
                </div>
              ))}

              {/* Data rows */}
              {filteredInventors.map(inv => {
                const inventorRow = filteredPivot.get(inv.id);
                if (!inventorRow || inventorRow.size === 0) return null;
                return [
                  // Row header with checkbox
                  <div key={`row-${inv.id}`} style={{
                    background: '#fff', padding: 8, fontSize: 11, fontWeight: 600,
                    color: '#0F172A', fontFamily: 'Poppins, sans-serif',
                    display: 'flex', alignItems: 'center', gap: 4,
                    overflow: 'hidden',
                  }}>
                    <input
                      type="checkbox"
                      checked={isInventorFullySelected(inv.id)}
                      onChange={() => toggleInventorSelection(inv.id)}
                      style={{ cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={inv.name}>
                      {inv.name.slice(0, 14)}
                    </span>
                  </div>,
                  // Company cells
                  ...companies.map(comp => {
                    const assignment = filteredPivot.get(inv.id)?.get(comp.id);
                    if (!assignment) {
                      return (
                        <div key={`cell-${inv.id}-${comp.id}`} style={{ background: '#F8FAFC' }} />
                      );
                    }
                    const lifecycleStatus = assignment.lifecycle_status || assignment.status || 'ai_suggested';
                    const style = STATUS_STYLES[lifecycleStatus] || STATUS_STYLES.ai_suggested;
                    const score = assignment.match_score ?? assignment.ai_score;
                    const ndaComplete = lifecycleStatus === 'countersigned' || lifecycleStatus === 'slot_booked' || lifecycleStatus === 'completed';
                    const tooltipText = [
                      inv.name,
                      assignment.event_submissions?.product_name || assignment.idea_name,
                      assignment.event_submissions?.category || assignment.category,
                      score != null ? `Score: ${score}%` : null,
                      style.label,
                    ].filter(Boolean).join(' | ');
                    return (
                      <div
                        key={`cell-${inv.id}-${comp.id}`}
                        style={{
                          background: '#fff', padding: '4px 8px', cursor: 'pointer',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                        }}
                        title={tooltipText}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                        onClick={() => {
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (next.has(assignment.id)) next.delete(assignment.id);
                            else next.add(assignment.id);
                            return next;
                          });
                        }}
                      >
                        {score != null && (
                          <span style={{ fontSize: 11, fontWeight: 600, color: scoreColor(score) }}>
                            {score}%
                          </span>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {/* Lifecycle status dot */}
                          <span style={{
                            width: 4, height: 4, borderRadius: '50%',
                            background: style.color, display: 'inline-block', flexShrink: 0,
                          }} />
                          {/* NDA icon: lock or check */}
                          <span style={{ fontSize: 10, color: ndaComplete ? '#10B981' : '#94A3B8' }}>
                            {ndaComplete ? '✓' : '🔒'}
                          </span>
                        </div>
                      </div>
                    );
                  }),
                ];
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
